import { useState } from "react"
import { Plus, Pencil, Trash2, Shield, ChevronDown, ChevronUp, X } from "lucide-react"
import {
  useGetRolesQuery, useCreateRoleMutation, useUpdateRoleMutation, useDeleteRoleMutation,
  useGetUsersQuery, useGetUserRolesQuery, useAssignRoleMutation, useRevokeRoleMutation,
  type AdminRole,
} from "../features/admin/adminApi"
import Modal from "../components/Modal"

// ── Role form modal ────────────────────────────────────────────────────────────
function RoleModal({ initial, onClose }: { initial?: AdminRole; onClose: () => void }) {
  const [name, setName] = useState(initial?.name ?? "")
  const [description, setDescription] = useState(initial?.description ?? "")
  const [createRole] = useCreateRoleMutation()
  const [updateRole] = useUpdateRoleMutation()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true); setError("")
    try {
      if (initial) {
        await updateRole({ uid: initial.id, name, description }).unwrap()
      } else {
        await createRole({ name, description }).unwrap()
      }
      onClose()
    } catch (err: any) {
      setError(err?.data?.message ?? "Failed to save")
    } finally { setSaving(false) }
  }

  return (
    <Modal title={initial ? "Edit Role" : "New Role"} onClose={onClose} size="sm">
      {error && <p className="mb-3 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
          <input value={name} onChange={e => setName(e.target.value)} required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ── User role assignment drawer ────────────────────────────────────────────────
function UserRoleDrawer({ userUid, userName, onClose }: { userUid: string; userName: string; onClose: () => void }) {
  const { data: allRoles = [] } = useGetRolesQuery()
  const { data: userRolesData, isLoading } = useGetUserRolesQuery(userUid)
  const [assignRole] = useAssignRoleMutation()
  const [revokeRole] = useRevokeRoleMutation()

  const assigned = userRolesData?.roles ?? []
  const assignedIds = new Set(assigned.map(r => r.id))

  return (
    <Modal title={`Roles for ${userName}`} onClose={onClose} size="sm">
      {isLoading ? <p className="text-sm text-gray-500">Loading…</p> : (
        <div className="space-y-2">
          {allRoles.map(role => {
            const isAssigned = assignedIds.has(role.id)
            return (
              <div key={role.id} className="flex items-center justify-between px-3 py-2.5 rounded-lg border border-gray-200">
                <div>
                  <p className="text-sm font-medium text-gray-900 capitalize">{role.name}</p>
                  {role.description && <p className="text-xs text-gray-400">{role.description}</p>}
                </div>
                <button
                  onClick={() => isAssigned ? revokeRole({ uid: userUid, roleUid: role.id }) : assignRole({ uid: userUid, roleUid: role.id })}
                  className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                    isAssigned ? "bg-red-100 text-red-700 hover:bg-red-200" : "bg-green-100 text-green-700 hover:bg-green-200"
                  }`}
                >
                  {isAssigned ? "Revoke" : "Assign"}
                </button>
              </div>
            )
          })}
        </div>
      )}
    </Modal>
  )
}

export default function RolesPage() {
  const { data: roles = [], isLoading } = useGetRolesQuery()
  const [deleteRole] = useDeleteRoleMutation()
  const [modal, setModal] = useState<"create" | AdminRole | null>(null)
  const [expandedRole, setExpandedRole] = useState<string | null>(null)

  // User assignment section
  const [userSearch, setUserSearch] = useState("")
  const [userSearchInput, setUserSearchInput] = useState("")
  const [drawerUser, setDrawerUser] = useState<{ uid: string; name: string } | null>(null)
  const { data: usersData } = useGetUsersQuery({ search: userSearch, limit: 10 } as any)
  const users = usersData?.users ?? []

  const handleDelete = async (role: AdminRole) => {
    if (!window.confirm(`Delete role "${role.name}"?`)) return
    try { await deleteRole(role.id).unwrap() }
    catch (err: any) { alert(err?.data?.message ?? "Cannot delete") }
  }

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-bold text-gray-900">Roles Management</h1>

      {/* Role list */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2"><Shield size={16} /> Roles</h2>
          <button onClick={() => setModal("create")}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700">
            <Plus size={14} /> New Role
          </button>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-sm text-gray-400">Loading…</div>
        ) : roles.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-400">No roles yet. Create one above.</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {roles.map(role => (
              <li key={role.id}>
                <div className="flex items-center justify-between px-5 py-3 hover:bg-gray-50">
                  <button className="flex items-center gap-2 text-left flex-1 min-w-0"
                    onClick={() => setExpandedRole(expandedRole === role.id ? null : role.id)}>
                    <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold uppercase flex-shrink-0">
                      {role.name[0]}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 capitalize">{role.name}</p>
                      {role.description && <p className="text-xs text-gray-400 truncate">{role.description}</p>}
                    </div>
                    {expandedRole === role.id ? <ChevronUp size={14} className="text-gray-400 ml-auto" /> : <ChevronDown size={14} className="text-gray-400 ml-auto" />}
                  </button>
                  <div className="flex items-center gap-1 ml-4 flex-shrink-0">
                    <button onClick={() => setModal(role)} className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => handleDelete(role)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                {expandedRole === role.id && (
                  <div className="px-5 pb-3">
                    <p className="text-xs text-gray-400">
                      Created: {role.createdAt ? new Date(role.createdAt).toLocaleDateString() : "—"}
                    </p>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Assign roles to users */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Assign Roles to Users</h2>
          <p className="text-xs text-gray-400 mt-0.5">Search a user and manage their roles</p>
        </div>
        <div className="p-5">
          <form onSubmit={e => { e.preventDefault(); setUserSearch(userSearchInput) }} className="flex gap-2 mb-4">
            <input
              value={userSearchInput}
              onChange={e => setUserSearchInput(e.target.value)}
              placeholder="Search by name or email…"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700">Search</button>
            {userSearch && <button type="button" onClick={() => { setUserSearch(""); setUserSearchInput("") }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"><X size={14} /></button>}
          </form>
          {users.length > 0 && (
            <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
              {users.map(u => (
                <li key={u.id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{u.firstName} {u.lastName}</p>
                    <p className="text-xs text-gray-400">{u.email}</p>
                  </div>
                  <button onClick={() => setDrawerUser({ uid: u.id, name: `${u.firstName} ${u.lastName}` })}
                    className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-blue-100 hover:text-blue-700">
                    Manage Roles
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Modals */}
      {modal === "create" && <RoleModal onClose={() => setModal(null)} />}
      {modal && modal !== "create" && <RoleModal initial={modal as AdminRole} onClose={() => setModal(null)} />}
      {drawerUser && <UserRoleDrawer userUid={drawerUser.uid} userName={drawerUser.name} onClose={() => setDrawerUser(null)} />}
    </div>
  )
}
